import React, { useEffect, useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useOnboardingStore } from "../store/useOnboardingStore";
import login_bg from "../assets/images/signup_bg.webp";
import logo from "../assets/images/logo.webp";

/* ══════════════════════════════════════════════════
   INJECT STYLES
══════════════════════════════════════════════════ */
const injectKF = () => {
  if (document.getElementById("sg-kf")) return;
  const s = document.createElement("style");
  s.id = "sg-kf";
  s.innerHTML = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

    @keyframes slideInBot {
      from { opacity:0; transform:translateY(-8px) scale(0.96); }
      to   { opacity:1; transform:translateY(0)    scale(1); }
    }
    @keyframes botCursor { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes speakBar  { 0%,100%{transform:scaleY(1)} 50%{transform:scaleY(1.9)} }
    @keyframes pulseDot  { 0%,100%{opacity:1} 50%{opacity:.35} }
    @keyframes botBobSvg { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-2.5px)} }
    @keyframes floatHeartL1 {
      0%  { transform:translate(0,0)         scale(1);    opacity:1; }
      100%{ transform:translate(-14px,-54px) scale(0.18); opacity:0; }
    }
    @keyframes floatHeartL2 {
      0%  { transform:translate(0,0)        scale(0.82); opacity:0.9; }
      100%{ transform:translate(-4px,-58px) scale(0.12); opacity:0; }
    }
    @keyframes floatHeartL3 {
      0%  { transform:translate(0,0)       scale(0.68); opacity:0.8; }
      100%{ transform:translate(9px,-50px) scale(0.22); opacity:0; }
    }
    @keyframes floatHeartR1 {
      0%  { transform:translate(0,0)        scale(1);    opacity:1; }
      100%{ transform:translate(14px,-54px) scale(0.18); opacity:0; }
    }
    @keyframes floatHeartR2 {
      0%  { transform:translate(0,0)       scale(0.82); opacity:0.9; }
      100%{ transform:translate(4px,-58px) scale(0.12); opacity:0; }
    }
    @keyframes floatHeartR3 {
      0%  { transform:translate(0,0)        scale(0.68); opacity:0.8; }
      100%{ transform:translate(-9px,-50px) scale(0.22); opacity:0; }
    }

    /* ── Bot SVG classes ── */
    .sg-bot-bob-g { animation: botBobSvg 3s ease-in-out infinite; }
    .sg-hl1 { animation: floatHeartL1 2s 0.0s  ease-out infinite; transform-origin: 23px 27px; }
    .sg-hl2 { animation: floatHeartL2 2s 0.65s ease-out infinite; transform-origin: 23px 27px; }
    .sg-hl3 { animation: floatHeartL3 2s 1.3s  ease-out infinite; transform-origin: 23px 27px; }
    .sg-hr1 { animation: floatHeartR1 2s 0.2s  ease-out infinite; transform-origin: 49px 27px; }
    .sg-hr2 { animation: floatHeartR2 2s 0.85s ease-out infinite; transform-origin: 49px 27px; }
    .sg-hr3 { animation: floatHeartR3 2s 1.5s  ease-out infinite; transform-origin: 49px 27px; }

    /* ── Bot popup wrapper ── */
    .sg-bot-wrap {
      position: fixed;
      z-index: 99999;
      max-width: calc(100vw - 20px);
      box-sizing: border-box;
      animation: slideInBot .28s cubic-bezier(.34,1.2,.64,1) both;
    }
    .sg-bot-wrap::before {
      content: '';
      position: absolute;
      width: 16px; height: 9px;
      background: #c0001a;
      clip-path: polygon(50% 0%,100% 100%,0% 100%);
    }
    .sg-bot-wrap.pinned::before  { display: none; }
    .sg-bot-wrap.below::before   { top:-9px;    left: var(--arrow-off, 24px); margin-left: -8px; }
    .sg-bot-wrap.above::before   { bottom:-9px; left: var(--arrow-off, 24px); margin-left: -8px; transform:rotate(180deg); }
    .sg-bot-wrap.toright::before { top: var(--arrow-off, 24px); left:-9px;  margin-top: -4.5px; transform:rotate(-90deg); }
    .sg-bot-wrap.toleft::before  { top: var(--arrow-off, 24px); right:-9px; margin-top: -4.5px; transform:rotate(90deg); }

    /* ── Bot card ── */
    .sg-bot-popup {
      background: #fff;
      border-radius: 14px;
      border: 1px solid rgba(0,0,0,.07);
      overflow: hidden;
      box-shadow:
        0 4px 8px rgba(0,0,0,.06),
        0 18px 52px rgba(0,0,0,.18),
        0 2px 10px rgba(192,0,26,.1);
    }
    .sg-bp-top {
      background: linear-gradient(135deg,#c0001a 0%,#8b0000 58%,#5c0000 100%);
      padding: clamp(9px,2.6vw,12px) clamp(10px,3vw,14px) clamp(8px,2.4vw,10px);
      display: flex; align-items: center; gap: clamp(7px,2vw,10px);
      position: relative; overflow: visible;
    }
    .sg-bp-top::after {
      content: ''; position: absolute; inset: 0; pointer-events: none;
      background-image:
        linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px);
      background-size: 16px 16px;
    }
    .sg-bp-avatar { position: relative; flex-shrink: 0; overflow: visible; }
    .sg-bp-avatar svg { width: clamp(46px,12vw,64px); height: clamp(46px,12vw,64px); display: block; }
    .sg-speak-bars {
      display: flex; align-items: flex-end; gap: 2px;
      position: absolute; bottom: -3px; right: -4px;
      background: rgba(0,0,0,.3); border-radius: 4px; padding: 2px 3px; z-index: 2;
    }
    .sg-sbar { width: 2.5px; border-radius: 2px; background: #fff; transform-origin: bottom; }
    .sg-sbar:nth-child(1){ height:4px;  animation:speakBar .48s .00s ease-in-out infinite; }
    .sg-sbar:nth-child(2){ height:9px;  animation:speakBar .48s .10s ease-in-out infinite; }
    .sg-sbar:nth-child(3){ height:6px;  animation:speakBar .48s .20s ease-in-out infinite; }
    .sg-sbar:nth-child(4){ height:3px;  animation:speakBar .48s .30s ease-in-out infinite; }
    .sg-bp-meta { flex: 1; z-index: 1; min-width: 0; overflow: hidden; }
    .sg-bp-name { font-size: clamp(10px,2.8vw,11.5px); font-weight: 800; color: #fff; letter-spacing: .06em; font-family: 'DM Mono','Fira Code',monospace; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .sg-bp-sub  { font-size: clamp(8.5px,2.4vw,9.5px); color: rgba(255,255,255,.7); display: flex; align-items: center; gap: 4px; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .sg-bp-sdot { width: 5px; height: 5px; border-radius: 50%; background: #6effa0; box-shadow: 0 0 4px rgba(100,255,150,.85); flex-shrink: 0; animation: pulseDot 2s ease-in-out infinite; }
    .sg-bp-badge{ z-index: 1; flex-shrink: 0; background: rgba(255,255,255,.16); border: 1px solid rgba(255,255,255,.3); border-radius: 20px; padding: 2px clamp(6px,2vw,8px); font-size: clamp(9px,2.4vw,10px); font-weight: 700; color: #fff; white-space: nowrap; }

    .sg-bp-tag     { background: linear-gradient(90deg,#fff5f5,#fff); border-bottom: 1px solid rgba(192,0,26,.1); padding: 5px clamp(10px,3vw,14px); display: flex; align-items: center; gap: 6px; }
    .sg-bp-tag-dot { width: 5px; height: 5px; border-radius: 50%; background: #c0001a; flex-shrink: 0; }
    .sg-bp-tag-txt { font-size: clamp(8.5px,2.4vw,9.5px); font-weight: 800; color: #c0001a; letter-spacing: .08em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

    .sg-bp-body { padding: clamp(8px,2.6vw,10px) clamp(10px,3vw,14px) clamp(6px,2vw,8px); }
    .sg-bp-msg  { font-size: clamp(12px,3.4vw,13px); line-height: 1.62; color: #1a1a1a; word-break: break-word; margin: 0; }
    .sg-bp-msg strong { color: #c0001a; font-weight: 800; }
    .sg-bp-cursor { display: inline-block; width: 2px; height: 13px; background: #c0001a; border-radius: 2px; margin-left: 2px; vertical-align: middle; animation: botCursor .65s ease-in-out infinite; }

    .sg-bp-foot { padding: clamp(7px,2.2vw,8px) clamp(10px,3vw,14px) clamp(9px,2.8vw,11px); display: flex; align-items: center; justify-content: space-between; gap: 8px; border-top: 1px solid rgba(0,0,0,.05); background: #fafafa; flex-wrap: wrap; }
    .sg-bp-dots { display: flex; gap: 4px; align-items: center; }
    .sg-bpd     { width: 6px; height: 6px; border-radius: 50%; background: #e0e0e0; transition: all .3s; flex-shrink: 0; }
    .sg-bpd.on  { width: 18px; border-radius: 999px; background: linear-gradient(90deg,#c0001a,#8b0000); }
    .sg-bp-skip { background: none; border: none; cursor: pointer; font-size: clamp(10px,2.8vw,11px); color: #c0c0c0; padding: 4px 6px; border-radius: 6px; transition: all .18s; white-space: nowrap; }
    .sg-bp-skip:hover { background: #f0f0f0; color: #888; }
    .sg-bp-next { background: linear-gradient(135deg,#c0001a,#8b0000); color: #fff; border: none; border-radius: 8px; padding: 7px clamp(12px,3.6vw,16px); font-size: clamp(11px,3vw,12px); font-weight: 800; cursor: pointer; letter-spacing: .03em; box-shadow: 0 3px 12px rgba(192,0,26,.32); transition: all .18s; white-space: nowrap; }
    .sg-bp-next:hover  { transform: translateY(-1px); box-shadow: 0 5px 16px rgba(192,0,26,.46); }
    .sg-bp-next:active { transform: scale(.97); }

    /* ── Page-level responsive tweaks ── */
    .sg-navbar-logo { height: clamp(28px,7vw,40px); width: auto; }
    .sg-hero-title  { font-size: clamp(1.5rem,5vw,2.25rem); }
    .sg-hero-sub    { font-size: clamp(.85rem,2.6vw,1rem); }

    @media (max-width: 575.98px) {
      .sg-bp-foot { justify-content: center; }
      .sg-bp-foot > div { width: 100%; justify-content: space-between; }
    }
  `;
  document.head.appendChild(s);
};

/* ══════════════════════════════════════════════════
   BOT FACE SVG
══════════════════════════════════════════════════ */
const BotFaceSVG: React.FC = () => (
  <svg viewBox="0 0 72 72" fill="none" style={{ overflow: "visible" }}>
    <defs>
      <style>{`
        .sg-bot-bob-g { animation: botBobSvg 3s ease-in-out infinite; }
        .sg-hl1 { animation: floatHeartL1 2s 0.0s  ease-out infinite; transform-origin: 23px 27px; }
        .sg-hl2 { animation: floatHeartL2 2s 0.65s ease-out infinite; transform-origin: 23px 27px; }
        .sg-hl3 { animation: floatHeartL3 2s 1.3s  ease-out infinite; transform-origin: 23px 27px; }
        .sg-hr1 { animation: floatHeartR1 2s 0.2s  ease-out infinite; transform-origin: 49px 27px; }
        .sg-hr2 { animation: floatHeartR2 2s 0.85s ease-out infinite; transform-origin: 49px 27px; }
        .sg-hr3 { animation: floatHeartR3 2s 1.5s  ease-out infinite; transform-origin: 49px 27px; }
      `}</style>
    </defs>
    <g className="sg-bot-bob-g">
      <line x1="36" y1="10" x2="36" y2="3" stroke="rgba(255,255,255,0.82)" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="36" cy="2" r="3.2" fill="rgba(255,255,255,0.9)" />
      <rect x="7" y="10" width="58" height="46" rx="15" fill="rgba(255,255,255,0.97)" />
      <circle cx="7"  cy="32" r="5.5" fill="rgba(255,255,255,0.85)" stroke="rgba(192,0,26,0.45)" strokeWidth="1.2" />
      <circle cx="65" cy="32" r="5.5" fill="rgba(255,255,255,0.85)" stroke="rgba(192,0,26,0.45)" strokeWidth="1.2" />
      <rect x="11" y="17" width="24" height="20" rx="9" fill="#c0001a" />
      <rect x="37" y="17" width="24" height="20" rx="9" fill="#8b0000" />
      <ellipse cx="23" cy="27" rx="9" ry="8" fill="white" />
      <ellipse cx="49" cy="27" rx="9" ry="8" fill="white" />
      <g className="sg-hl1"><path d="M23,33 C23,33 12,23 12,17 C12,13 17,11 23,17 C29,11 34,13 34,17 C34,23 23,33 23,33Z" fill="#ff3d6b" /></g>
      <g className="sg-hl2"><path d="M23,33 C23,33 12,23 12,17 C12,13 17,11 23,17 C29,11 34,13 34,17 C34,23 23,33 23,33Z" fill="#c0001a" opacity="0.88" /></g>
      <g className="sg-hl3"><path d="M23,33 C23,33 12,23 12,17 C12,13 17,11 23,17 C29,11 34,13 34,17 C34,23 23,33 23,33Z" fill="#ff7099" /></g>
      <g className="sg-hr1"><path d="M49,33 C49,33 38,23 38,17 C38,13 43,11 49,17 C55,11 60,13 60,17 C60,23 49,33 49,33Z" fill="#ff3d6b" /></g>
      <g className="sg-hr2"><path d="M49,33 C49,33 38,23 38,17 C38,13 43,11 49,17 C55,11 60,13 60,17 C60,23 49,33 49,33Z" fill="#c0001a" opacity="0.88" /></g>
      <g className="sg-hr3"><path d="M49,33 C49,33 38,23 38,17 C38,13 43,11 49,17 C55,11 60,13 60,17 C60,23 49,33 49,33Z" fill="#ff7099" /></g>
      <path d="M16,46 Q36,60 56,46" stroke="#c0001a" strokeWidth="2.8" strokeLinecap="round" fill="none" />
      <ellipse cx="12" cy="44" rx="6" ry="4" fill="rgba(255,100,100,0.2)" />
      <ellipse cx="60" cy="44" rx="6" ry="4" fill="rgba(255,100,100,0.2)" />
      <line x1="7"  y1="28" x2="2"  y2="23" stroke="rgba(255,255,255,0.78)" strokeWidth="2.4" strokeLinecap="round" />
      <line x1="7"  y1="36" x2="2"  y2="39" stroke="rgba(255,255,255,0.78)" strokeWidth="2.4" strokeLinecap="round" />
      <line x1="65" y1="28" x2="70" y2="23" stroke="rgba(255,255,255,0.78)" strokeWidth="2.4" strokeLinecap="round" />
      <line x1="65" y1="36" x2="70" y2="39" stroke="rgba(255,255,255,0.78)" strokeWidth="2.4" strokeLinecap="round" />
    </g>
  </svg>
);

/* ══════════════════════════════════════════════════
   TYPES + STEP DATA
══════════════════════════════════════════════════ */
type BotSide = "below" | "above" | "toright" | "toleft";

interface StepData {
  tag:    string;
  plain:  string;
  html:   React.ReactNode;
  sideLg: BotSide;
}

const STEPS: StepData[] = [
  {
    tag:    "STEP 1 OF 3 — WELCOME",
    plain:  "Already a member? Sign in here and jump back into your matches!",
    html:   <>Already a member? <strong>Sign in here</strong> and jump back into your matches!</>,
    sideLg: "below",
  },
  {
    tag:    "STEP 2 OF 3 — YOUR NAME",
    plain:  "Tell us your name — it's the first step to finding your perfect match!",
    html:   <>Tell us your <strong>name</strong> — it's the first step to finding your perfect match!</>,
    sideLg: "below",
  },
  {
    tag:    "STEP 3 OF 3 — LET'S GO!",
    plain:  "All set! Hit Start My Journey to begin your story!",
    html:   <>All set! Hit <strong>Start My Journey</strong> to begin your story!</>,
    sideLg: "above",
  },
];

const TOTAL = STEPS.length;

/* ══════════════════════════════════════════════════
   BOT POPUP
══════════════════════════════════════════════════ */
interface BotPopupProps {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  step:      number;
  data:      StepData;
  onSkip:    () => void;
  onNext:    () => void;
  nextLabel: string;
}

const BotPopup: React.FC<BotPopupProps> = ({ anchorRef, step, data, onSkip, onNext, nextLabel }) => {
  const GAP = 10;

  const [pos,        setPos]        = useState<{ top:number; left:number; width:number; arrowOff:number } | null>(null);
  const [activeSide, setActiveSide] = useState<BotSide>("below");
  const [pinnedMobile, setPinnedMobile] = useState(false);
  const [displayed,  setDisplayed]  = useState("");
  const [done,       setDone]       = useState(false);
  const [status,     setStatus]     = useState("typing...");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* FIX: the popup card's height was a hardcoded guess (POPUP_H). The
     real rendered card is often shorter/taller than that guess, so
     "above"/"below" math left gaps that other page elements showed
     through, making the popup look disconnected from its actual
     target. A ResizeObserver measures the real card height instead —
     read via a ref (not state) so it never forces calcPos to be
     recreated / the retry-loop below to reset mid-measurement. */
  const cardRef       = useRef<HTMLDivElement>(null);
  const measuredHRef  = useRef<number | null>(null);
  const [hasMeasured, setHasMeasured] = useState(false);

  const calcPos = useCallback((): boolean => {
    if (!anchorRef.current) return false;
    const r  = anchorRef.current.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    if (r.width === 0 && r.height === 0) return false;

    /* Responsive width & popup-height estimate across ALL breakpoints —
       measuredHRef (the real rendered height) always wins once known. */
    const PW      = vw < 768 ? Math.min(vw - 24, 420) : vw < 1100 ? Math.min(vw - 24, 280) : 310;
    const POPUP_H = measuredHRef.current ?? (vw < 400 ? 270 : vw < 768 ? 250 : 240);
    /* Side-anchored popups need enough horizontal room on both sides; below that, fall back */
    const allowSide = vw >= 1100;

    if (vw < 768) {
      /* FIX: previously this also checked whether the popup would
         visually overlap ANY other input/button/header/nav on the
         page — on a short form, almost everything sits within that
         range, so the check nearly always failed and the popup fell
         back to being pinned at the bottom of the viewport,
         disconnected from the field it was meant to point at (e.g.
         step 1's "Sign in" popup landing near the bottom of the page
         instead of under the Sign In button in the header).

         Simple, robust replacement: try directly below the anchor,
         then directly above it, and only pin to the bottom as a last
         resort when neither fits on a very short screen. */
      const belowTop = r.bottom + GAP;
      const aboveTop = r.top - POPUP_H - GAP;

      let top: number;
      let side: BotSide;
      let pinned = false;

      if (belowTop + POPUP_H <= vh - 8) {
        top = belowTop; side = "below";
      } else if (aboveTop >= 8) {
        top = aboveTop; side = "above";
      } else {
        top = Math.max(8, vh - POPUP_H - 16);
        side = "below";
        pinned = true;
      }

      const left = Math.max(12, (vw - PW) / 2);
      const anchorCenterX = r.left + r.width / 2;
      const arrowOff = Math.max(20, Math.min(anchorCenterX - left, PW - 20));
      setPinnedMobile(pinned);
      setActiveSide(side);
      setPos({ top, left, width: PW, arrowOff });
      return true;
    }
    setPinnedMobile(false);

    let side = data.sideLg, top = r.top + r.height / 2, left = 0;
    if (!allowSide && (side === "toright" || side === "toleft")) side = "below";

    if (side === "toright") {
      left = r.right + GAP;
      if (left + PW > vw - 12) { left = r.left - PW - GAP; side = "toleft"; }
      top = Math.min(top, vh - POPUP_H - 8); top = Math.max(top, 8);
    } else if (side === "toleft") {
      left = r.left - PW - GAP;
      if (left < 12) { left = r.right + GAP; side = "toright"; }
      top = Math.min(top, vh - POPUP_H - 8); top = Math.max(top, 8);
    } else if (side === "above") {
      top  = r.top - POPUP_H - GAP; left = r.left;
      if (top < 8) { top = r.bottom + GAP; side = "below"; }
      if (left + PW > vw - 8) left = vw - PW - 8;
    } else {
      top = r.bottom + GAP; left = r.left;
      if (left + PW > vw - 8) left = vw - PW - 8;
      if (top + POPUP_H > vh - 8) { top = r.top - POPUP_H - GAP; side = "above"; if (top < 8) top = 8; }
    }
    left = Math.max(12, Math.min(left, vw - PW - 12));
    top  = Math.max(8, Math.min(top, vh - POPUP_H - 8));

    const arrowOff = (side === "toright" || side === "toleft")
      ? Math.max(20, Math.min((r.top + r.height / 2) - top, POPUP_H - 20))
      : Math.max(20, Math.min((r.left + r.width / 2) - left, PW - 20));

    setActiveSide(side); setPos({ top, left, width: PW, arrowOff }); return true;
  }, [anchorRef, data.sideLg]);

  /* Keep a ref to the latest calcPos so the (mount-once) ResizeObserver
     below always calls the current version instead of a stale one. */
  const calcPosRef = useRef(calcPos);
  useEffect(() => { calcPosRef.current = calcPos; }, [calcPos]);

  /* Measure the popup card's real height whenever it changes (typing
     animation growing the text, step change, viewport resize, etc.)
     and immediately recompute the position from that real height. */
  useEffect(() => {
    if (!cardRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) measuredHRef.current = entry.contentRect.height;
      setHasMeasured(true);
      calcPosRef.current();
    });
    ro.observe(cardRef.current);
    return () => ro.disconnect();
  }, []);

  /* Retry until anchor visible */
  useEffect(() => {
    let attempts = 0;
    if (retryRef.current) clearTimeout(retryRef.current);
    const tryCalc = () => { const ok = calcPos(); if (!ok && attempts++ < 50) retryRef.current = setTimeout(tryCalc, 80); };
    tryCalc();
    window.addEventListener("resize", calcPos);
    window.addEventListener("scroll", calcPos, true);
    return () => {
      if (retryRef.current) clearTimeout(retryRef.current);
      window.removeEventListener("resize", calcPos);
      window.removeEventListener("scroll", calcPos, true);
    };
  }, [calcPos, step]);

  /* Typewriter */
  useEffect(() => {
    setDisplayed(""); setDone(false); setStatus("typing...");
    /* Reset the measurement for the new step's message so the old
       step's height is never briefly reused for the new one. */
    measuredHRef.current = null; setHasMeasured(false);
    let i = 0;
    const tick = () => {
      if (i <= data.plain.length) { setDisplayed(data.plain.slice(0, i++)); timerRef.current = setTimeout(tick, 21); }
      else { setDone(true); setStatus("Online — here to guide you!"); }
    };
    tick();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [data.plain, step]);

  /* FIX: this used to `return null` until `pos` existed — but `pos`
     depends on the real measured height, which can only come from the
     ResizeObserver AFTER the card has mounted. That was a deadlock:
     the card never mounted early enough to be measured, so real
     measurement never happened and the math silently fell back to the
     hardcoded guess forever. Instead, always mount the card — just
     keep it invisible/non-interactive until we have both a computed
     position AND a real measurement — so it can be measured, then
     revealed already correctly placed. */
  const ready  = pos !== null && hasMeasured;
  const isSide = activeSide === "toright" || activeSide === "toleft";

  return (
    <div
      className={`sg-bot-wrap ${activeSide}${pinnedMobile ? " pinned" : ""}`}
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        width: pos?.width ?? 300,
        visibility: ready ? "visible" : "hidden",
        pointerEvents: ready ? "auto" : "none",
        transform: isSide ? "translateY(-50%)" : "none",
        ["--arrow-off" as any]: `${pos?.arrowOff ?? 24}px`,
      }}
    >
      <div ref={cardRef} className="sg-bot-popup" style={{ width: "100%" }}>
        <div className="sg-bp-top">
          <div className="sg-bp-avatar">
            <BotFaceSVG />
            <div className="sg-speak-bars">
              <div className="sg-sbar"/><div className="sg-sbar"/>
              <div className="sg-sbar"/><div className="sg-sbar"/>
            </div>
          </div>
          <div className="sg-bp-meta">
            <div className="sg-bp-name">SLAMMMM GUIDE</div>
            <div className="sg-bp-sub"><div className="sg-bp-sdot"/><span>{status}</span></div>
          </div>
          <div className="sg-bp-badge">{step} / {TOTAL}</div>
        </div>
        <div className="sg-bp-tag">
          <div className="sg-bp-tag-dot"/>
          <div className="sg-bp-tag-txt">{data.tag}</div>
        </div>
        <div className="sg-bp-body">
          {done
            ? <p className="sg-bp-msg">{data.html}</p>
            : <p className="sg-bp-msg">{displayed}<span className="sg-bp-cursor"/></p>
          }
        </div>
        <div className="sg-bp-foot">
          <div className="sg-bp-dots">
            {Array.from({ length: TOTAL }).map((_, i) => (
              <div key={i} className={`sg-bpd${i + 1 === step ? " on" : ""}`}/>
            ))}
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button className="sg-bp-skip" onClick={onSkip}>Skip guide</button>
            <button
              className="sg-bp-next" onClick={onNext}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = "none"; }}
            >{nextLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   SIGNUP PAGE
══════════════════════════════════════════════════ */
const Signup: React.FC = () => {
  const { inputs, handleInputChange, validatefullName, errors } = useOnboardingStore();
  const navigate = useNavigate();

  const [guideStep, setGuideStep] = useState<number | null>(null);
  /* ── auto-advance lock ── */
  const autoAdvancedRef = useRef<Set<number>>(new Set());

  const ref1 = useRef<HTMLDivElement>(null); // Sign in button
  const ref2 = useRef<HTMLDivElement>(null); // Name input
  const ref3 = useRef<HTMLDivElement>(null); // Submit button
  const refs = [ref1, ref2, ref3];

  useEffect(() => {
    injectKF();
    const seen = localStorage.getItem("signupGuide");
    if (!seen) {
      localStorage.setItem("signupGuide", "true");
      setTimeout(() => setGuideStep(1), 900);
    }
  }, []);

  /* Scroll anchor into view */
  useEffect(() => {
    if (guideStep === null) return;
    refs[guideStep - 1]?.current?.scrollIntoView({ behavior:"smooth", block:"center" });
  }, [guideStep]);

  const handleNext = useCallback(
    () => setGuideStep(p => (p !== null && p < TOTAL) ? p + 1 : null),
    []
  );
  const handleSkip = useCallback(() => setGuideStep(null), []);

  /* ── AUTO-ADVANCE: name type karo → bot step 3 pe ── */
  const autoAdvanceBot = useCallback((targetStep: number) => {
    if (autoAdvancedRef.current.has(targetStep)) return;
    autoAdvancedRef.current.add(targetStep);
    setTimeout(() => {
      setGuideStep(prev => {
        if (prev === targetStep - 1 || prev === targetStep) return targetStep;
        return prev;
      });
    }, 550);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validatefullName()) {
      navigate("/onboarding", { state: { fullname: inputs["first_name"] } });
    }
  };

  return (
    <div style={{ overflowX:"hidden" }}>

      {/* ══ NO overlay, NO blur, NO glow border — bot floats freely ══ */}

      {/* Bot popup */}
      {guideStep !== null && guideStep >= 1 && guideStep <= TOTAL && (
        <BotPopup
          anchorRef={refs[guideStep - 1]}
          step={guideStep}
          data={STEPS[guideStep - 1]}
          onSkip={handleSkip}
          onNext={guideStep < TOTAL ? handleNext : handleSkip}
          nextLabel={guideStep < TOTAL ? "Next →" : "Done ✓"}
        />
      )}

      {/* ════════════ NAVBAR ════════════ */}
      <nav className="navbar navbar-expand-lg navbar-light border-bottom bg-white">
        <div className="container">
          <Link to="/" className="navbar-brand">
            <img src={logo} alt="Logo" className="sg-navbar-logo" />
          </Link>

          {/* Step 1 anchor — Sign in button */}
          <div ref={ref1} className="ms-auto" style={{ position:"relative", zIndex: guideStep === 1 ? 9500 : 1 }}>
            <Link to="/login" className="btn btn-outline-primary btn-sm">
              Sign in
            </Link>
          </div>
        </div>
      </nav>

      {/* ════════════ HERO SECTION ════════════ */}
      <section className="py-4 py-md-5">
        <div className="container">
          <div className="row align-items-center g-4">

            {/* Left image — hidden on small screens to save vertical space */}
            <div className="col-lg-6 d-none d-lg-block">
              <img
                src={login_bg}
                className="img-fluid rounded-4"
                alt="Signup background"
              />
            </div>

            {/* Right form */}
            <div className="col-lg-5 col-md-9 col-sm-11 col-12 offset-lg-1 mx-auto mx-lg-0">
              <div className="auth_caption py-3 py-lg-0">
                <h2 className="fw-bold mb-2 sg-hero-title">
                  Join the <span style={{ color:"#c0001a" }}>Movement</span>
                </h2>
                <p className="text-muted mb-4 sg-hero-sub">Every connection starts with a name.</p>

                <form onSubmit={handleSubmit} noValidate>

                  {/* Step 2: Name input */}
                  <div
                    ref={ref2}
                    className="mb-3"
                    style={{ position:"relative", zIndex: guideStep === 2 ? 9500 : 1 }}
                  >
                    <label className="form-label fw-semibold text-muted small mb-1">
                      Your Name
                    </label>
                    <input
                      type="text"
                      className={`form-control form-control-lg${errors["first_name"] ? " is-invalid" : ""}`}
                      placeholder="Enter your name"
                      value={inputs["first_name"] || ""}
                      onChange={e => {
                        handleInputChange("first_name", e.target.value);
                        /* Name type karo → bot step 3 pe auto-advance */
                        if (e.target.value.trim().length >= 2) autoAdvanceBot(3);
                      }}
                    />
                    {errors["first_name"] && (
                      <div className="invalid-feedback">{errors["first_name"]}</div>
                    )}
                  </div>

                  {/* Step 3: Submit button */}
                  <div
                    ref={ref3}
                    style={{ position:"relative", zIndex: guideStep === 3 ? 9500 : 1 }}
                  >
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg w-100"
                      style={{
                        background:    "linear-gradient(135deg,#c0001a,#8b0000)",
                        border:        "none",
                        fontWeight:    700,
                        letterSpacing: "0.03em",
                        boxShadow:     "0 4px 16px rgba(192,0,26,0.35)",
                      }}
                    >
                      Start My Journey
                    </button>
                  </div>

                </form>

                <p className="text-center text-muted mt-4 mb-0 small">
                  Already have an account?{" "}
                  <Link to="/login" style={{ color:"#c0001a", fontWeight:600 }}>
                    Sign in here
                  </Link>
                </p>

              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default Signup;